import { useNewChatAction } from '@/hooks/aiChat/useNewChatAction';
import { Button, ButtonProps } from '@electricjs/arc';
import { CHAT_NAMESPACES } from '@/types/aiChat';
import { useTranslation } from 'react-i18next';

type NewChatButtonProps = {
  /**
   * Button ID for testing and analytics
   */
  id?: string;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * The namespace key for the chat to clear
   * @default CHAT_NAMESPACES.GIGAWATT_COPILOTKIT
   */
  namespaceKey?: string;
  /**
   * Callback fired after the chat is cleared
   */
  onAfterClear?: () => void;
  /**
   * Optional loading state to disable button
   */
  isLoading?: boolean;
} & Partial<ButtonProps>;

/**
 * Button to start a new chat conversation
 *
 * Clears the current chat state and allows the user to start fresh.
 * The next message sent will automatically create a new conversation.
 *
 * The button is automatically disabled when a message is loading or streaming
 * to prevent clearing chat state during an active conversation.
 *
 * @example
 * ```tsx
 * // In Gigawatt header (default namespace)
 * <NewChatButton id="new-chat-header-button" />
 *
 * // In drawer with custom styling
 * <NewChatButton
 *   id="new-chat-drawer-button"
 *   className={customStyles}
 * />
 *
 * // In IT Support chat with different namespace
 * <NewChatButton
 *   id="it-support-new-chat-button"
 *   namespaceKey="it-support"
 * />
 * ```
 */
export const NewChatButton = ({
  id = 'new-chat-button',
  className,
  namespaceKey = CHAT_NAMESPACES.GIGAWATT_COPILOTKIT,
  onAfterClear,
  isLoading: externalIsLoading,
  ...buttonProps
}: NewChatButtonProps) => {
  const { t } = useTranslation('ai-chat');
  const { handleNewChat, isDisabled } = useNewChatAction(
    namespaceKey,
    externalIsLoading
  );

  const handleClick = () => {
    handleNewChat();
    // Call the callback after clearing
    if (onAfterClear) {
      // Use setTimeout to ensure state updates have propagated
      setTimeout(() => {
        onAfterClear();
      }, 50);
    }
  };

  return (
    <Button
      id={id}
      onClick={handleClick}
      disabled={isDisabled}
      aria-label={t('newChatAriaLabel')}
      testId={id}
      className={className}
      iconBefore="pencil"
      {...buttonProps}>
      {t('newChat')}
    </Button>
  );
};
